import React from 'react';
import { ProductHeader } from './components/ProductHeader';
import { CtaFigmaForStorytelling } from './components/CtaFigmaForStorytelling';
import { TransformationShowcase } from './components/TransformationShowcase';
import { Footer } from '../components/shared/Footer';
import { RxArrowRight, RxVideo } from 'react-icons/rx';
import { FaYoutube, FaBlog, FaNewspaper } from 'react-icons/fa';
import { ImageWithZoom } from "../components/shared/ImageModal";

export default function Product() {
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <ProductHeader />
      <CtaFigmaForStorytelling />

      {/* Story Forge Process Preview Section */}
      <section className="py-16 px-4 bg-gray-900 border-t border-blue-500/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-400 mb-6">
            <span className="text-sm font-medium">Preview</span>
          </div>
          <h2 className="mb-5 text-4xl font-bold md:mb-6 md:text-5xl lg:text-6xl leading-[1.2] pb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400 flex items-center justify-center gap-4">
            <img src="/assets/images/product/story_forge.png" alt="Story Forge Icon" className="inline-block h-10 w-10 md:h-12 md:w-12 lg:h-14 lg:w-14 align-middle" />
            The Story Forge Process
          </h2>
          <p className="md:text-lg text-gray-300 max-w-2xl mx-auto mb-8">
            Meet the Story Forge wizard: your AI-powered creative partner. Build your narrative from the ground up with guided story structures, explore the 36 dramatic situations, and organize your ideas into actionable scene cards. The wizard helps you brainstorm, structure, and expand your universe—step by step.
          </p>
          <div className="flex flex-col items-center">
            <a href="/assets/images/learn/omni_preview.png" target="_blank" rel="noopener noreferrer">
              <img
                src="/assets/images/learn/omni_preview.png"
                alt="Story Forge Process Preview"
                className="mx-auto my-6 max-w-xs md:max-w-md lg:max-w-2xl cursor-zoom-in rounded-lg shadow-lg border border-purple-400/30 transition-transform hover:scale-105"
              />
            </a>
            <span className="text-xs text-gray-400">Click to view full size in new tab</span>
          </div>
        </div>
      </section>

      <TransformationShowcase />

      {/* Hero Section */}
      <section className="py-20 px-4 max-w-7xl mx-auto">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 mb-6">
            <span className="text-sm font-medium">Coming Soon</span>
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
            Never Lose Continuity in Your Storytelling
          </h1>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
            Omnigeist helps you transform your creative ideas into rich, interconnected
            narratives across all media types with consistent results.
          </p>

          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mb-12">
            <a 
              href="/contact?type=product" 
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
            >
              Join Beta Waitlist
              <RxArrowRight className="text-xl" />
            </a>
            <button 
              className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
              onClick={() => {/* Add demo video modal logic */}}
            >
              Watch Demo
              <RxVideo className="text-xl" />
            </button>
          </div>

          {/* Product Journey Section */}
          <div className="grid md:grid-cols-2 gap-8 text-left bg-gray-800/50 rounded-xl p-8">
            <div>
              <h2 className="text-2xl font-bold mb-4">Our Journey</h2>
              <p className="text-gray-300 mb-6">
                For the past year, our growing team has been crafting a revolutionary platform 
                for creative professionals. We're excited to share our progress and invite you 
                to be part of our journey.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center mt-1">
                    <FaYoutube className="text-blue-400" />
                  </div>
                  <p className="text-gray-300">Follow our development journey on YouTube</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center mt-1">
                    <FaBlog className="text-purple-400" />
                  </div>
                  <p className="text-gray-300">Read about our latest updates on our blog</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mt-1">
                    <FaNewspaper className="text-green-400" />
                  </div>
                  <p className="text-gray-300">Subscribe to our newsletter for early access</p>
                </div>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4">Release Timeline</h2>
              <div className="space-y-6">
                <div className="relative pl-8 border-l-2 border-blue-500/30">
                  <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-blue-500"></div>
                  <h3 className="font-semibold text-blue-400">Alpha Testing</h3>
                  <p className="text-gray-300">Internal testing and refinement</p>
                </div>
                <div className="relative pl-8 border-l-2 border-purple-500/30">
                  <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-purple-500"></div>
                  <h3 className="font-semibold text-purple-400">Beta Program</h3>
                  <p className="text-gray-300">Limited access for early adopters</p>
                </div>
                <div className="relative pl-8 border-l-2 border-green-500/30">
                  <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-green-500"></div>
                  <h3 className="font-semibold text-green-400">Public Launch</h3>
                  <p className="text-gray-300">Full platform availability</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features Section: Emphasizing benefits */}
      <section className="py-16 px-4 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">
            How Omnigeist Helps You Create More
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              title="Intelligent Story Creation"
              features={[
                "Overcome writer's block and jumpstart your imagination with AI.",
                "Structure your narratives with AI-powered wizards.",
                "Create immersive worlds with story beats and dramatic situations.",
                "Develop characters that feel alive and compelling with integrated character tools"
              ]}
            />
           <FeatureCard
              title="Professional-Grade Organization"
              features={[
                "Manage complex universes with easy-to-use hierarchy management tools.",
                "Track story progress and milestones with built-in timelines.",
                "Maintain story integrity and consistency across all formats.",
                 "Merge and combine stories with ease, maintaining continuity."
              ]}
            />
            <FeatureCard
              title="World-Building Tools"
              features={[
                "Generate detailed maps and locations.",
                "Map out complex relationships for dynamic interactions.",
                "Build engaging storylines with AI-driven beat creation tools.",
                "Define the rules of your universe for consistency and realism."
              ]}
            />
          </div>
        </div>
      </section>

      {/* Target Audiences Section: More action-oriented titles */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">
            Who Can Create With Omnigeist?
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
             <AudienceCard
              title="For Aspiring Creators"
              audiences={[
                "Aspiring writers and storytellers ready to share their ideas.",
                "Creative writing enthusiasts eager to explore new possibilities.",
                "World-builders and universe creators who need a tool to manage and scale their worlds",
                "Fan fiction authors who want to expand their skills in narrative.",
                "Role-playing game creators looking for tools to enhance their world building"
              ]}
            />
            <AudienceCard
              title="For Industry Professionals"
              audiences={[
                "Professional authors looking to increase their output",
                "Entertainment industry professionals who need to ensure multi-platform continuity",
                "Game developers building complex and immersive game worlds",
                "Educational institutions looking for new ways to teach creative writing skills",
                "Writing groups and communities looking for tools to enhance their projects and creativity"
              ]}
            />
          </div>
        </div>
      </section>

      {/* Benefits Section: More descriptive and engaging */}
      <section className="py-16 px-4 bg-gray-800">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">
            Unlock the Power of AI in Storytelling
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <BenefitCard
              title="For Writers"
              benefits={[
                "Go from a blank page to a complex narrative in less time with integrated tools.",
                "Utilize industry best practices in story structure to enhance your narratives",
                "Maintain consistency across every scene and chapter.",
                "Join forces with other creative minds.",
                 "Create large and detailed universes that come alive in your stories."
              ]}
            />
            <BenefitCard
              title="For Creative Teams"
               benefits={[
                "Centrally manage stories and plot lines for all writers.",
                 "Ensure canon-consistent continuity across multiple works.",
                 "Collaborate  and easily share assets.",
                 "Use version control and track changes through time.",
                "Scale your storytelling process with confidence"
              ]}
            />
            <BenefitCard
              title="For Publishers/Studios"
             benefits={[
              "Easily expand your IP with innovative new tools.",
              "Maintain a high level of canon through AI support",
              "Develop stories for a wide range of formats.",
              "Protect your intellectual properties.",
                "Connect with audiences through deep and immersive storytelling."
             ]}
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// Component for feature cards
const FeatureCard = ({ title, features }) => (
  <div className="bg-gray-700 p-6 rounded-lg">
    <h3 className="text-xl font-bold mb-4">{title}</h3>
    <ul className="space-y-2">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <span className="text-green-400 mr-2">•</span>
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Component for audience cards
const AudienceCard = ({ title, audiences }) => (
  <div className="bg-gray-700 p-6 rounded-lg">
    <h3 className="text-xl font-bold mb-4">{title}</h3>
    <ul className="space-y-2">
      {audiences.map((audience, index) => (
        <li key={index} className="flex items-start">
          <span className="text-blue-400 mr-2">•</span>
          <span>{audience}</span>
        </li>
      ))}
    </ul>
  </div>
);

// Component for benefit cards
const BenefitCard = ({ title, benefits }) => (
  <div className="bg-gray-700 p-6 rounded-lg">
    <h3 className="text-xl font-bold mb-4">{title}</h3>
    <ul className="space-y-2">
      {benefits.map((benefit, index) => (
        <li key={index} className="flex items-start">
          <span className="text-purple-400 mr-2">•</span>
          <span>{benefit}</span>
        </li>
      ))}
    </ul>
  </div>
);